import type { MutableRefObject } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import type { SessionOutcome } from '@/lib/practice/session-types';

/**
 * What the crash fallback says, given how many concepts were rated before
 * the runner threw. The orchestrator flushes those ratings as an incomplete
 * session on the way here, and writes nothing when there were none — so the
 * copy follows that rule exactly rather than promising a record that may not
 * exist.
 */
export function runnerCrashMessage(rated: number): string {
  const lead = 'Something went wrong with this session.';
  if (rated === 0) {
    return `${lead} Nothing was lost — you hadn't rated any concepts yet.`;
  }
  const saved =
    rated === 1
      ? 'Your 1 rated concept was saved'
      : `Your ${rated} rated concepts were saved`;
  return `${lead} ${saved} to history as an incomplete session.`;
}

/**
 * Fallback for the error boundary around the practice runner. The boundary
 * wraps only the runner, so the page header and the history sidebar survive
 * the crash and the user has a way back that is not a reload.
 *
 * Reads the orchestrator's live-outcomes ref at render rather than taking a
 * count: the boundary renders this on the crash itself, before the host has
 * re-rendered, and the ref is the one value that is already current then —
 * it is the same snapshot the partial flush records.
 */
export function RunnerErrorFallback({
  outcomesRef,
  onBackToSetup,
}: {
  outcomesRef: MutableRefObject<SessionOutcome[]>;
  onBackToSetup: () => void;
}) {
  return (
    <div
      role='alert'
      className='flex h-full flex-col items-center justify-center gap-3 px-6 py-16 text-center'
    >
      <AlertTriangle className='h-5 w-5 text-destructive' aria-hidden />
      <p className='max-w-md text-sm text-foreground'>
        {runnerCrashMessage(outcomesRef.current.length)}
      </p>
      <Button variant='outline' onClick={onBackToSetup}>
        Back to setup
      </Button>
    </div>
  );
}
